/**
 * Dialect Strings
 * Motivational content in local dialects for human-centric moments only
 * NOT for UI labels, settings, or technical text
 */

import { DialectStrings, SupportedDialect } from "../types";

// Twi (Ghana)
const tw: DialectStrings = {
  welcome: [
    "Akwaaba! Yɛnom nsuo biara 💧",
    "Ɛte sɛn? Wunom nsuo anaa?",
  ],
  motivation: [
    "Yɛ no yie! Nsuo ma wo ho tew ✨",
    "Toa so! Wo yɛ ade pa",
    "Nsuo yɛ nkwa 💧",
  ],
  success: [
    "Ayekoo! Wo goal no adu 🎉",
    "Wo yɛ adwuma pa!",
    "Wo ho tew koraa! ✨",
  ],
  reminders: [
    "Ɛberɛ a wobɛnom nsuo 💧",
    "Ma yɛn nom nsuo bi",
    "Wo streak no hwehwɛ wo 🔥",
  ],
  streaks: [
    "Streak no refire! 🔥",
    "Toa so saa ara!",
    "Wo di adwene pa! 💪",
  ],
};

// Hausa
const ha: DialectStrings = {
  welcome: [
    "Barka da zuwa! Sha ruwa 💧",
    "Yaya dai? An sha ruwa?",
  ],
  motivation: [
    "Yawa! Ruwa yana da kyau ✨",
    "Ci gaba! Kana yi kyau",
    "Ruwa shi ne rai 💧",
  ],
  success: [
    "Taya murna! Ka cimma burin ka 🎉",
    "Ka yi aiki mai kyau!",
    "Ka cika lafiya! ✨",
  ],
  reminders: [
    "Lokaci ne don sha ruwa 💧",
    "Mu sha ruwa kaɗan",
    "Streak ɗinka yana jira 🔥",
  ],
  streaks: [
    "Streak na ƙone! 🔥",
    "Ka ci gaba haka!",
    "Kana da ƙarfi! 💪",
  ],
};

// Swahili
const sw: DialectStrings = {
  welcome: [
    "Karibu! Kunywa maji 💧",
    "Habari? Umekunywa maji?",
  ],
  motivation: [
    "Vizuri sana! Maji ni afya ✨",
    "Endelea! Unafanya vizuri",
    "Maji ni uhai 💧",
  ],
  success: [
    "Hongera! Umefika lengo lako 🎉",
    "Umefanya kazi nzuri!",
    "Una afya njema! ✨",
  ],
  reminders: [
    "Ni wakati wa kunywa maji 💧",
    "Tunywe maji kidogo",
    "Mfululizo wako unakungoja 🔥",
  ],
  streaks: [
    "Mfululizo unawaka moto! 🔥",
    "Endelea hivyo!",
    "Una nguvu! 💪",
  ],
};

// Yoruba
const yo: DialectStrings = {
  welcome: [
    "Ẹ káàbọ̀! Ẹ mu omi 💧",
    "Báwo ni? Ṣé o ti mu omi?",
  ],
  motivation: [
    "O dáa gan! Omi dára fún ara ✨",
    "Máa lọ síwájú! O n ṣe dáadáa",
    "Omi ni ìyè 💧",
  ],
  success: [
    "Kú oríire! O ti dé ibi tí o fẹ́ 🎉",
    "O ti ṣe iṣẹ́ dáadáa!",
    "Ara rẹ̀ yá! ✨",
  ],
  reminders: [
    "Àkókò ti tó láti mu omi 💧",
    "Ẹ jẹ́ ká mu omi díẹ̀",
    "Streak rẹ̀ n dúró dè ọ́ 🔥",
  ],
  streaks: [
    "Streak rẹ̀ n jó! 🔥",
    "Máa bá a lọ!",
    "O lagbara! 💪",
  ],
};

// Filipino (Tagalog)
const tl: DialectStrings = {
  welcome: [
    "Maligayang pagdating! Uminom ng tubig 💧",
    "Kumusta? Nakainom ka na ba?",
  ],
  motivation: [
    "Ang galing! Tubig ay kalusugan ✨",
    "Tuloy lang! Magaling ka",
    "Tubig ay buhay 💧",
  ],
  success: [
    "Congrats! Naabot mo ang goal mo 🎉",
    "Magaling ang trabaho mo!",
    "Healthy ka ngayon! ✨",
  ],
  reminders: [
    "Oras na para uminom ng tubig 💧",
    "Tara inom tayo",
    "Hinihintay ka ng streak mo 🔥",
  ],
  streaks: [
    "Nag-aalab ang streak mo! 🔥",
    "Keep it up!",
    "Malakas ka! 💪",
  ],
};

export const dialects: Record<SupportedDialect, DialectStrings> = {
  tw,
  ha,
  sw,
  yo,
  tl,
};

/**
 * Get a random motivational message from dialect
 */
export function getDialectMotivation(dialect: SupportedDialect): string {
  const messages = dialects[dialect]?.motivation || [];
  return messages[Math.floor(Math.random() * messages.length)] || "";
}

/**
 * Get a random success message from dialect
 */
export function getDialectSuccess(dialect: SupportedDialect): string {
  const messages = dialects[dialect]?.success || [];
  return messages[Math.floor(Math.random() * messages.length)] || "";
}

/**
 * Get a random reminder message from dialect
 */
export function getDialectReminder(dialect: SupportedDialect): string {
  const messages = dialects[dialect]?.reminders || [];
  return messages[Math.floor(Math.random() * messages.length)] || "";
}

/**
 * Get a random welcome message from dialect
 */
export function getDialectWelcome(dialect: SupportedDialect): string {
  const messages = dialects[dialect]?.welcome || [];
  return messages[Math.floor(Math.random() * messages.length)] || "";
}

/**
 * Get a random streak message from dialect
 */
export function getDialectStreak(dialect: SupportedDialect): string {
  const messages = dialects[dialect]?.streaks || [];
  return messages[Math.floor(Math.random() * messages.length)] || "";
}
